"use no memo";

// @tanstack/react-table v8 keeps its state in a mutable `table` instance whose identity is stable
// across updates, so React Compiler memoization would skip re-renders that the table needs. Remove
// this opt-out when the table upgrades to v9, which is compiler-safe.
import type { Row, RowSelectionState, Table } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import Link from "ui/components/Link";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import dataTableStyles from "./DataTable.module.scss";
import styles from "./Multiselect.module.scss";
import type { DataTableRow } from "./types";

export function MultiSelectHeader<T>({
  table,
  rowSelection,
  totalCount,
  currentCount,
}: {
  table: Table<T>;
  rowSelection: RowSelectionState;
  totalCount: number;
  currentCount: number;
}) {
  const { tooltip, checkedState, onCheckedChange } = useMemo(() => {
    const selectedRowCount = Object.keys(rowSelection).length;
    let tooltip = "";
    let checkedState: boolean | "indeterminate";
    let onCheckedChange = () => table.toggleAllRowsSelected();
    if (selectedRowCount === totalCount) {
      checkedState = true;
      tooltip = "Deselect all rows";
      onCheckedChange = () => table.resetRowSelection();
    } else if (selectedRowCount) {
      checkedState = "indeterminate";
      tooltip = "Select all rows";
      if (currentCount < totalCount) {
        // Check if all rows on this page are selected
        tooltip =
          currentCount === table.getSelectedRowModel().rows.length
            ? "Deselect all rows on this page"
            : "Select all rows on this page";
      }
    } else {
      checkedState = false;
      tooltip = "Select all rows";
    }
    return { tooltip, checkedState, onCheckedChange };
  }, [rowSelection, totalCount, currentCount]);

  return wrapWithTooltip(
    <div className={cx(dataTableStyles.dummyCell, dataTableStyles.multiSelectCell)}>
      <Checkbox checked={checkedState} onCheckedChange={onCheckedChange} data-testid="select-all-checkbox" />
    </div>,
    tooltip,
  );
}

export function MultiSelectMenu<T extends {}>({
  table,
  multiSelect,
  rowSelection,
  containerRef,
  hasControls,
}: {
  table: Table<T>;
  multiSelect: DataTableRow<T>["multiSelect"];
  rowSelection: RowSelectionState; // source of truth for selected rows
  // Lets the parent measure the menu height so the sticky header can offset below it.
  containerRef?: (node: HTMLDivElement | null) => void;
  // When the table also renders a controls bar, the menu sticks below the controls instead of over them.
  hasControls?: boolean;
}) {
  const [selectedRows, setSelectedRows] = useState<Row<T>[]>([]);

  useEffect(() => {
    const { rowsById } = table.getRowModel();
    const nextSelectedRows = table.getSelectedRowModel().rows;
    // Keep still-selected rows from other pages (for server-side pagination) and add the rows selected
    // in the current table. De-duplicate by row id so a row that reappears across renders/views is
    // counted once rather than accumulating (which otherwise inflates the "N selected" count).
    setSelectedRows((prev) => {
      const offPageSelected = prev.filter((row) => rowSelection[row.id] && !rowsById[row.id]);
      const byId = new Map<string, Row<T>>();
      for (const row of [...offPageSelected, ...nextSelectedRows]) {
        byId.set(row.id, row);
      }
      return [...byId.values()];
    });
  }, [table, rowSelection]);

  if (!selectedRows.length) {
    return null;
  }
  return (
    <div
      ref={containerRef}
      className={cx(styles.multiSelectMenu, hasControls && styles.hasControls)}
      data-testid="multiselect-menu"
    >
      <Link onClick={() => table.resetRowSelection()} className={styles.rowsSelected}>
        {selectedRows.length} selected
      </Link>
      {multiSelect?.(selectedRows)}
    </div>
  );
}
